import React, { useCallback, useContext, useMemo, useRef } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import auth, { AuthContext, type AuthContextValue } from '~/auth';
import { NavLink } from '~/lib/react-router-dom';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
} from '@mui/material';

import { NAVIGATION_LINK_IDS } from '~/consts/navigation';
import { TENANT_COLOURS } from '~/consts/tenant';

import useAppNavigation from '~/hooks/useAppNavigation';
import useNavigationLinks from '~/hooks/useNavigationLinks';
import useToggleState from '~/hooks/useToggleState';

import { filterNavigationLinksForTenant, isNavigationPathSelected } from '~/utils/navigationLinks';
import { openLabPortal } from '~/utils/openLabPortal';

import NavigationLayoutMenuItem from '../AppChrome/NavigationLayoutMenuItem';
import ThemeModeMenuItem from '../AppChrome/ThemeModeMenuItem';
import { StickyVerifyCustomer } from '../StickyVerifyCustomer';
import TenantSelect from '../TenantSelect';

import NavDropdown from './NavDropdown';
import ProductsDropdown from './ProductsDropdown';

interface SiteHeaderProps {
  tenant: App.Tenant;
}

function getDropdownItems(navLink: App.NavigationLink) {
  return [
    ...(navLink.href ? [{ href: navLink.href, label: navLink.label }] : []),
    ...(navLink.subLinks?.map((subLink) => ({
      href: subLink.href,
      label: subLink.label,
      section: subLink.section,
    })) ?? []),
  ];
}

function SiteHeader({ tenant }: SiteHeaderProps) {
  const { user } = useContext<AuthContextValue>(AuthContext);
  const { pathname } = useLocation();
  const { push } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();
  const navLinks = useNavigationLinks(user);

  const filteredNavLinks = useMemo(() => filterNavigationLinksForTenant(navLinks, tenant), [navLinks, tenant]);

  const userMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const { isToggled: isDrawerOpen, toggleOff: closeDrawer, toggle: toggleDrawer } = useToggleState();
  const { isToggled: isUserMenuOpen, toggleOn: openUserMenu, toggleOff: closeUserMenu } = useToggleState();

  const logUserOut = useCallback(() => {
    void auth
      .logout()
      .then(() => {
        push('/');
      })
      .catch((error) => {
        enqueueSnackbar(error instanceof Error ? error.message : 'Log out failed', {
          variant: 'error',
        });
      });
  }, [enqueueSnackbar, push]);

  return (
    <>
      <StickyVerifyCustomer />
      <AppBar
        position="static"
        sx={{
          background: TENANT_COLOURS[tenant.value].bg,
          color: TENANT_COLOURS[tenant.value].fg,
          zIndex: 9,
        }}
      >
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Toolbar sx={{ px: { xs: '4px', md: 0 } }}>
              <IconButton color="inherit" edge="start" onClick={toggleDrawer} sx={{ display: { md: 'none' } }}>
                <MenuIcon />
              </IconButton>

              <Box pr={[1, 2]} sx={{ display: 'flex', alignItems: 'center' }}>
                <TenantSelect size="large" />
              </Box>

              <ProductsDropdown />

              {!!filteredNavLinks.length && (
                <Box display={{ xs: 'none', md: 'block' }}>
                  {filteredNavLinks.map((navLink, navLinkIndex) => {
                    const hasSubLinks = !!navLink.subLinks?.length;
                    const isLabLink = navLink.id === NAVIGATION_LINK_IDS.LAB;

                    return (
                      <Box
                        key={`${navLink.href}-${navLinkIndex}`}
                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                      >
                        {!hasSubLinks && isLabLink && (
                          <Button color="inherit" onClick={() => void openLabPortal()} sx={{ textTransform: 'none' }}>
                            {navLink.label}
                          </Button>
                        )}

                        {!hasSubLinks && !isLabLink && (
                          <Button color="inherit" to={navLink.href} component={NavLink} sx={{ textTransform: 'none' }}>
                            {navLink.label}
                          </Button>
                        )}

                        {hasSubLinks && <NavDropdown buttonLabel={navLink.label} items={getDropdownItems(navLink)} />}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Toolbar>

            <Toolbar sx={{ px: { xs: 0 } }}>
              {!user && (
                <Button to="/login" component={NavLink} sx={{ textTransform: 'none' }}>
                  Login
                </Button>
              )}
              {!!user && (
                <>
                  <Button
                    className="T-account"
                    color="inherit"
                    ref={userMenuTriggerRef}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={openUserMenu}
                    sx={{ textTransform: 'none' }}
                  >
                    {user.givenName}
                  </Button>

                  <Menu
                    anchorEl={userMenuTriggerRef.current}
                    open={isUserMenuOpen}
                    autoFocus={false}
                    MenuListProps={{
                      subheader: <ListSubheader>{user.fullName}</ListSubheader>,
                    }}
                    onClose={closeUserMenu}
                  >
                    <MenuItem to="/profile" component={NavLink} onClick={closeUserMenu}>
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText>Account Settings</ListItemText>
                    </MenuItem>

                    <NavigationLayoutMenuItem onClick={closeUserMenu} />
                    <ThemeModeMenuItem onClick={closeUserMenu} />

                    <Divider />

                    <MenuItem onClick={logUserOut}>
                      <ListItemIcon>
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText>Log out</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Toolbar>
          </Stack>
        </Container>
      </AppBar>

      <Drawer
        open={isDrawerOpen}
        variant="temporary"
        onClose={closeDrawer}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box sx={{ width: '240px' }}>
          {!!filteredNavLinks.length && (
            <List>
              {filteredNavLinks.map((navLink, navLinkIndex) => {
                const isLabLink = navLink.id === NAVIGATION_LINK_IDS.LAB;

                return (
                  <React.Fragment key={`${navLink.href}-${navLinkIndex}`}>
                    {navLink.href && (
                      <ListItem disablePadding>
                        {isLabLink ? (
                          <ListItemButton
                            onClick={() => {
                              closeDrawer();
                              void openLabPortal();
                            }}
                          >
                            <ListItemText primary={navLink.label} />
                          </ListItemButton>
                        ) : (
                          <ListItemButton
                            selected={isNavigationPathSelected(pathname, navLink.href)}
                            to={navLink.href}
                            component={NavLink}
                            onClick={closeDrawer}
                          >
                            <ListItemText primary={navLink.label} />
                          </ListItemButton>
                        )}
                      </ListItem>
                    )}

                    {navLink.subLinks?.map((subLink, subLinkIndex) => (
                      <ListItem key={`${subLink.href}-${subLinkIndex}`} disablePadding>
                        <ListItemButton
                          selected={isNavigationPathSelected(pathname, subLink.href)}
                          to={subLink.href}
                          component={NavLink}
                          onClick={closeDrawer}
                          sx={{ pl: 4 }}
                        >
                          <ListItemText primary={subLink.label} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Drawer>
    </>
  );
}

export { SiteHeader };
